<template>
	<div>
		<div class="col-md-12">
			<h3 class="page-header">
				<i class="fa fa-file-text-o animated bounceInDown show-info"></i>
				Employee Statements
			</h3>
		</div>

		<div class="col-md-12">
			<div class="row stock_section">
				<div class="col-md-4">
					<label>Employee</label>
					<select v-model="employeeId" class="form-control form-cascade-control input-box-short" @change="clearStatement">
						<option value="">Select employee</option>
						<option v-for="person in employees" :key="person.employee_id" :value="person.employee_id">
							{{ person.name }}{{ person.employee_code ? ' · ' + person.employee_code : '' }}
						</option>
					</select>
				</div>
				<div class="col-md-2">
					<label>From date</label>
					<input v-model="fromDate" type="date" class="form-control form-cascade-control input-box-short" @change="clearStatement" />
				</div>
				<div class="col-md-2">
					<label>To date</label>
					<input v-model="toDate" type="date" class="form-control form-cascade-control input-box-short" @change="clearStatement" />
				</div>
				<div class="col-md-4 statement-actions">
					<button class="btn btn-primary" :disabled="loading" @click="showStatement">
						<i class="fa fa-search"></i> {{ loading ? 'Loading…' : 'Show statement' }}
					</button>
					<button class="btn btn-danger" :disabled="!statementLoaded || loading || generatingPdf" @click="generatePdf">
						<i class="fa fa-file-pdf-o"></i> {{ generatingPdf ? 'Generating…' : 'Download PDF' }}
					</button>
				</div>
			</div>
		</div>

		<div v-if="error" class="col-md-12" style="margin-top:10px">
			<div class="alert alert-danger">{{ error }}</div>
		</div>

		<div v-if="statementLoaded" class="col-md-12" style="margin-top:10px">
			<div ref="statement" class="statement-export">
				<div class="statement-heading">
					<div>
						<h3>V.S. DigiWorld</h3>
						<h4>Employee Salary Statement</h4>
					</div>
					<div class="statement-meta">
						<strong>{{ employee.name || selectedEmployeeName }}</strong>
						<span v-if="employee.employee_code">Employee code: {{ employee.employee_code }}</span>
						<span>{{ formatDate(fromDate) }} to {{ formatDate(toDate) }}</span>
					</div>
				</div>

				<div class="statement-summary">
					<div><span>Total salary</span><strong>₹{{ money(statementTotals.salary) }}</strong></div>
					<div><span>Total payments</span><strong>₹{{ money(statementTotals.payments) }}</strong></div>
					<div><span>Current salary payable</span><strong>₹{{ money(salaryBalance) }}</strong></div>
				</div>

				<table class="table table-bordered table-striped statement-table">
					<thead>
						<tr>
							<th>#</th>
							<th>Date</th>
							<th>Particulars</th>
							<th>Narration</th>
							<th class="text-right">Salary</th>
							<th class="text-right">Payments</th>
							<th class="text-right">Balance</th>
						</tr>
					</thead>
					<tbody>
						<tr v-for="(row, index) in entries" :key="row.id">
							<td>{{ index + 1 }}</td>
							<td>{{ formatDate(row.entry_date) }}</td>
							<td>{{ entryLabel(row.entry_type) }}</td>
							<td>{{ row.narration || '—' }}</td>
							<td class="text-right" :class="{ 'negative-value': salaryOf(row) < 0 }">{{ signedMoney(salaryOf(row)) }}</td>
							<td class="text-right" :class="{ 'negative-value': paymentOf(row) < 0 }">{{ signedMoney(paymentOf(row)) }}</td>
							<td class="text-right"><strong>₹{{ money(row.balance) }}</strong></td>
						</tr>
						<tr v-if="!entries.length">
							<td colspan="7" class="text-center text-muted" style="padding:24px">No salary or payment entries in this period.</td>
						</tr>
					</tbody>
					<tfoot v-if="entries.length">
						<tr>
							<th colspan="4" class="text-right">Period totals</th>
							<th class="text-right">₹{{ money(statementTotals.salary) }}</th>
							<th class="text-right">₹{{ money(statementTotals.payments) }}</th>
							<th class="text-right">₹{{ money(closingBalance) }}</th>
						</tr>
					</tfoot>
				</table>

				<div class="pdf-generated-on">Generated {{ generatedAt }}</div>
			</div>
		</div>
	</div>
</template>

<script>
import html2canvas from 'html2canvas'
import jsPDF from 'jspdf'
import { payLedgerService, errorMessage } from './payrollService'
import payrollFormat from './payrollFormat'

const ENTRY_LABELS = {
	opening: 'Opening balance',
	salary_earned: 'Salary',
	payment_made: 'Payment',
	adjustment: 'Adjustment',
}

export default {
	name: 'PayrollEmployeeStatements',
	mixins: [payrollFormat],
	data() {
		const today = new Date()
		const yearAgo = new Date(today)
		yearAgo.setFullYear(yearAgo.getFullYear() - 1)
		return {
			employees: [],
			employeeId: '',
			fromDate: this.toIsoDate(yearAgo),
			toDate: this.toIsoDate(today),
			employee: {},
			entries: [],
			salaryBalance: 0,
			statementLoaded: false,
			loading: false,
			generatingPdf: false,
			error: '',
			generatedAt: '',
		}
	},
	computed: {
		selectedEmployeeName() {
			const person = this.employees.find((row) => String(row.employee_id) === String(this.employeeId))
			return person ? person.name : 'Employee'
		},
		statementTotals() {
			return this.entries.reduce(
				(totals, row) => {
					const salary = this.salaryOf(row)
					const payment = this.paymentOf(row)
					if (salary !== null) totals.salary += Number(salary)
					if (payment !== null) totals.payments += Number(payment)
					return totals
				},
				{ salary: 0, payments: 0 }
			)
		},
		closingBalance() {
			return this.entries.length ? this.entries[this.entries.length - 1].balance : 0
		},
	},
	mounted() {
		payLedgerService.list().then((data) => { this.employees = data || [] }).catch((e) => { this.error = errorMessage(e, 'Could not load employees.') })
	},
	methods: {
		clearStatement() { this.statementLoaded = false; this.error = '' },
		entryLabel(type) { return ENTRY_LABELS[type] || type },
		reverses(row) {
			if (row.entry_type !== 'adjustment') return null
			if (row.source_table === 'pay_slips') return 'salary'
			if (row.source_table === 'pay_payments') return 'payment'
			return null
		},
		salaryOf(row) {
			const reversed = this.reverses(row)
			if (reversed === 'salary') return -Number(row.amount)
			if (reversed === 'payment') return null
			return row.direction === 'credit' ? Number(row.amount) : null
		},
		paymentOf(row) {
			const reversed = this.reverses(row)
			if (reversed === 'payment') return -Number(row.amount)
			if (reversed === 'salary') return null
			return row.direction === 'debit' ? Number(row.amount) : null
		},
		signedMoney(value) {
			if (value === null) return '—'
			return value < 0 ? `−₹${this.money(-value)}` : `₹${this.money(value)}`
		},
		showStatement() {
			if (!this.employeeId) { this.error = 'Select an employee.'; return }
			if (!this.fromDate || !this.toDate || this.fromDate > this.toDate) { this.error = 'Choose a valid From and To date range.'; return }
			this.loading = true
			this.error = ''
			this.statementLoaded = false
			payLedgerService.statement(this.employeeId, { from_date: this.fromDate, to_date: this.toDate })
				.then((data) => {
					this.employee = data.employee || {}
					this.entries = data.entries || []
					this.salaryBalance = Number(data.salary_balance || 0)
					this.generatedAt = new Date().toLocaleString()
					this.statementLoaded = true
				})
				.catch((e) => { this.error = errorMessage(e, 'Could not load the employee statement.') })
				.finally(() => { this.loading = false })
		},
		async generatePdf() {
			if (!this.$refs.statement) return
			this.generatingPdf = true
			this.error = ''
			this.generatedAt = new Date().toLocaleString()
			try {
				await this.$nextTick()
				const canvas = await html2canvas(this.$refs.statement, { scale: 2, useCORS: true, backgroundColor: '#ffffff' })
				const pdf = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' })
				const pageWidth = pdf.internal.pageSize.getWidth()
				const pageHeight = pdf.internal.pageSize.getHeight()
				const margin = 8
				const imageWidth = pageWidth - margin * 2
				const imageHeight = (canvas.height * imageWidth) / canvas.width
				const pageBodyHeight = pageHeight - margin * 2
				const image = canvas.toDataURL('image/jpeg', 0.95)
				let offset = 0
				do {
					if (offset > 0) pdf.addPage()
					pdf.addImage(image, 'JPEG', margin, margin - offset, imageWidth, imageHeight)
					offset += pageBodyHeight
				} while (offset < imageHeight)
				const safeName = (this.employee.name || this.selectedEmployeeName).replace(/[^a-z0-9]+/gi, '-').replace(/^-|-$/g, '')
				pdf.save(`employee-statement-${safeName}-${this.fromDate}-to-${this.toDate}.pdf`)
			} catch (e) {
				this.error = 'Could not generate the PDF. Please try again.'
			} finally {
				this.generatingPdf = false
			}
		},
	},
}
</script>

<style scoped>
.statement-actions { margin-top:24px; white-space:nowrap; }
.statement-actions .btn { margin-right:5px; }
.statement-export { background:#fff; padding:18px; color:#222; }
.statement-heading { display:flex; justify-content:space-between; align-items:flex-start; border-bottom:2px solid #475c78; margin-bottom:12px; }
.statement-heading h3 { margin:0 0 4px; color:#475c78; }
.statement-heading h4 { margin:0 0 12px; }
.statement-meta { display:flex; flex-direction:column; text-align:right; line-height:1.7; }
.statement-summary { display:flex; gap:12px; margin-bottom:12px; }
.statement-summary > div { flex:1; border:1px solid #d9dee5; border-radius:4px; padding:8px 12px; display:flex; justify-content:space-between; }
.statement-summary span { color:#667085; }
.statement-table thead { background:#475c78; color:#fff; }
.statement-table th:first-child { width:42px; }
.statement-table th:nth-child(2) { width:105px; }
.statement-table th:nth-child(3) { width:120px; }
.statement-table th:nth-child(5), .statement-table th:nth-child(6), .statement-table th:nth-child(7) { width:125px; }
.negative-value { color:#c0392b; }
.pdf-generated-on { margin-top:8px; color:#777; font-size:10px; text-align:right; }
.text-right { text-align:right; }
.text-center { text-align:center; }
@media (max-width:767px) {
	.statement-actions { margin-top:10px; white-space:normal; }
	.statement-actions .btn { margin-bottom:5px; }
	.statement-heading, .statement-summary { display:block; }
	.statement-meta { text-align:left; margin-bottom:8px; }
	.statement-summary > div { margin-bottom:5px; }
}
</style>
